import { NO_SENSITIVITY, TRUST_FACTOR_KEYS, type TrustFactors, type TrustLevel, type TrustReason } from "./types";

/** Budget tiers. The SQL twin hardcodes the same numbers; rubric-cases.json pins both. */
export const BUDGET_G2_USD = 1000;
export const BUDGET_G3_USD = 250;

/**
 * The TS copy of public.trust_grade_from_factors. It exists only for live
 * previews; the stored grade always comes from the database trigger. Both are
 * pinned to __fixtures__/rubric-cases.json.
 */
export function budgetLevel(budgetUsd: number): TrustLevel {
  const b = Number.isFinite(budgetUsd) ? budgetUsd : 0;
  if (b >= BUDGET_G2_USD) return 2;
  if (b >= BUDGET_G3_USD) return 3;
  return NO_SENSITIVITY;
}

export function sanitizeTrustFactors(raw: unknown): TrustFactors {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: TrustFactors = {};
  for (const key of TRUST_FACTOR_KEYS) {
    const v = (raw as Record<string, unknown>)[key];
    if (v === 1 || v === 2 || v === 3 || v === 4) out[key] = v;
  }
  return out;
}

export function gradeFromFactors(
  factors: TrustFactors,
  budgetUsd: number,
): { grade: 1 | 2 | 3 | 4; specialSuggested: boolean; reasons: TrustReason[] } {
  const f = sanitizeTrustFactors(factors);
  const levels: Array<[TrustReason, TrustLevel]> = [
    ...TRUST_FACTOR_KEYS.map((k): [TrustReason, TrustLevel] => [k, f[k] ?? NO_SENSITIVITY]),
    ["budget", budgetLevel(budgetUsd)],
  ];
  const min = Math.min(...levels.map(([, l]) => l)) as TrustLevel;
  const atMin = levels.filter(([, l]) => l === min);
  // Level 4 means "none" and every simple task has several, so it never escalates.
  const grade = (min === 2 || min === 3) && atMin.length >= 2 ? ((min - 1) as 1 | 2) : min;
  const storedOnes = TRUST_FACTOR_KEYS.filter((k) => f[k] === 1).length;
  return {
    grade,
    specialSuggested: f.people === 1 || storedOnes >= 2,
    reasons: min < NO_SENSITIVITY ? atMin.map(([k]) => k) : [],
  };
}
